import { useEffect, useRef } from 'react';

import {
  Box,
  Card,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Image } from '@realadvisor/image';
import { format } from 'date-fns';
import { Home, Trash2 } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

export type TransactionPreviewItem = {
  propertyTitle: string;
  roomsInfo: string;
  location: string;
  photoUrl?: string | null;
  listingImage?: { bucket_name: string; file_name: string } | null;
  signatureDate?: Date | string | null;
  brokerName?: string | null;
  teamName?: string | null;
  isDraft?: boolean;
};

type TransactionPreviewCardProps = {
  item: TransactionPreviewItem;
  layout?: 'profile' | 'form';
  compact?: boolean;
  onRemove?: () => void;
  removeDisabled?: boolean;
  highlight?: boolean;
  highlightAnimation?: string;
};

const overlayChipSx = {
  borderRadius: '6px',
  px: 0.75,
  py: 0.375,
  fontSize: '0.65rem',
  fontWeight: 600,
  lineHeight: 1.2,
} as const;

export const TransactionPreviewCard = ({
  item,
  layout = 'profile',
  compact = false,
  onRemove,
  removeDisabled,
  highlight = false,
  highlightAnimation,
}: TransactionPreviewCardProps) => {
  const { t, dateLocale } = useLocale();
  const cardRef = useRef<HTMLDivElement>(null);
  const isFormLayout = layout === 'form';
  const inset = compact ? 8 : 10;

  useEffect(() => {
    if (highlight) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [highlight]);

  const formattedDate = (() => {
    if (!item.signatureDate) {
      return null;
    }
    try {
      return format(new Date(item.signatureDate), 'MMM yyyy', {
        locale: dateLocale,
      });
    } catch {
      return '-';
    }
  })();

  return (
    <Card
      ref={cardRef}
      variant="outlined"
      sx={{
        position: 'relative',
        display: 'flex',
        ...(isFormLayout
          ? {
              p: 1.5,
              gap: 2,
              alignItems: 'flex-start',
              width: '60%',
              bgcolor: 'background.paper',
            }
          : {
              p: 0,
              overflow: 'hidden',
              alignItems: 'stretch',
              width: '100%',
              bgcolor: item.isDraft ? 'grey.50' : 'background.paper',
              borderColor: item.isDraft ? 'primary.light' : 'grey.200',
              borderStyle: item.isDraft ? 'dashed' : 'solid',
              opacity: item.isDraft ? 0.95 : 1,
              ...(highlight && highlightAnimation
                ? { animation: highlightAnimation }
                : {}),
            }),
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: isFormLayout
            ? { xs: 96, sm: 140 }
            : compact
            ? 108
            : { xs: 112, sm: 132 },
          ...(isFormLayout
            ? { height: { xs: 96, sm: 100 }, borderRadius: 1 }
            : { alignSelf: 'stretch', minHeight: compact ? 88 : 96 }),
          flexShrink: 0,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          ...(!isFormLayout && {
            '& img': { width: '100%', height: '100%', objectFit: 'cover' },
          }),
        }}
      >
        {item.photoUrl != null ? (
          <Box
            component="img"
            src={item.photoUrl}
            alt={t('Property photo')}
            sx={
              isFormLayout
                ? { width: '100%', height: '100%', objectFit: 'cover' }
                : {
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }
            }
          />
        ) : item.listingImage != null ? (
          isFormLayout ? (
            <Image
              src={{
                bucketName: item.listingImage.bucket_name,
                fileName: item.listingImage.file_name,
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                '& img': {
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                },
              }}
            >
              <Image
                src={{
                  bucketName: item.listingImage.bucket_name,
                  fileName: item.listingImage.file_name,
                }}
              />
            </Box>
          )
        ) : (
          <Home
            size={isFormLayout ? 68 : compact ? 40 : 48}
            strokeWidth={isFormLayout ? 2 : 1.5}
            color={
              isFormLayout
                ? 'var(--mui-palette-grey-400)'
                : 'var(--rax-grey400)'
            }
          />
        )}
        <Box
          sx={{
            ...(isFormLayout
              ? {
                  position: 'absolute',
                  top: 6,
                  left: 6,
                  backgroundColor: '#dbeafe',
                  color: '#1d4ed8',
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  lineHeight: 1.25,
                }
              : {
                  ...overlayChipSx,
                  position: 'absolute',
                  top: inset,
                  left: inset,
                  zIndex: 2,
                  backgroundColor:
                    'rgba(var(--mui-palette-primary-mainChannel) / 0.16)',
                  color: 'var(--rax-blue700)',
                }),
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            component="span"
            sx={{
              width: isFormLayout ? 6 : 5,
              height: isFormLayout ? 6 : 5,
              borderRadius: '50%',
              bgcolor: isFormLayout ? '#3b82f6' : 'primary.main',
            }}
          />
          {t('SOLD')}
        </Box>
        {formattedDate && (
          <Box
            sx={{
              ...(isFormLayout
                ? {
                    position: 'absolute',
                    bottom: 6,
                    right: 6,
                    zIndex: 2,
                    background: 'var(--mui-palette-background-paper)',
                    borderRadius: 0.5,
                    px: 0.75,
                    py: 0.25,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    lineHeight: 1,
                  }
                : {
                    ...overlayChipSx,
                    position: 'absolute',
                    bottom: inset,
                    right: inset,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                  }),
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            {formattedDate}
          </Box>
        )}
      </Box>
      <Box
        sx={
          isFormLayout
            ? { flex: 1, minWidth: 0 }
            : {
                flex: 1,
                minWidth: 0,
                py: compact ? 1.25 : 1.5,
                pr: compact ? 1 : 1.25,
                pl: compact ? 1.25 : 1.75,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 0.5,
              }
        }
      >
        <Box
          sx={
            isFormLayout
              ? undefined
              : {
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }
          }
        >
          <Typography
            variant={
              isFormLayout ? 'subtitle1' : compact ? 'body2' : 'subtitle2'
            }
            color="text.primary"
            sx={{
              fontWeight: 600,
              lineHeight: isFormLayout ? 1.5 : 1.4,
              ...(isFormLayout
                ? {}
                : {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }),
            }}
          >
            {item.propertyTitle}
            {item.roomsInfo}
          </Typography>
          <Stack spacing={isFormLayout ? 0.5 : 0.25} sx={{ mt: 0.5 }}>
            <Typography
              variant={isFormLayout ? 'body2' : 'caption'}
              color="text.secondary"
              noWrap={!isFormLayout}
            >
              {item.location}
            </Typography>
            {item.brokerName && (
              <Typography
                variant={isFormLayout ? 'body2' : 'caption'}
                color="text.secondary"
                noWrap={!isFormLayout}
              >
                {t('Sold by')}{' '}
                <Box
                  component="span"
                  sx={{
                    color: 'primary.main',
                    fontWeight: isFormLayout ? 500 : 600,
                  }}
                >
                  {item.brokerName}
                </Box>
              </Typography>
            )}
            {item.teamName && (
              <Typography
                variant={isFormLayout ? 'body2' : 'caption'}
                color="text.secondary"
                noWrap={!isFormLayout}
              >
                {t('from')}{' '}
                <Box
                  component="span"
                  sx={{
                    color: 'text.primary',
                    fontWeight: isFormLayout ? 500 : 600,
                  }}
                >
                  {item.teamName}
                </Box>
              </Typography>
            )}
          </Stack>
        </Box>
        {onRemove != null && !item.isDraft && (
          <Tooltip title={t('Remove')}>
            <IconButton
              size="small"
              onClick={onRemove}
              disabled={removeDisabled}
              aria-label={t('Remove')}
              sx={{
                color: 'text.secondary',
                flexShrink: 0,
                mt: 0.25,
                '&:hover': { color: 'error.main' },
              }}
            >
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {!isFormLayout && item.isDraft && (
        <Box
          sx={{
            ...overlayChipSx,
            position: 'absolute',
            bottom: inset,
            right: inset,
            zIndex: 3,
            bgcolor: 'background.paper',
            color: 'primary.main',
            border: '1px solid',
            borderColor: 'primary.light',
            boxShadow:
              '0 1px 2px rgba(var(--mui-palette-common-blackChannel) / 0.06)',
          }}
        >
          {t('Draft')}
        </Box>
      )}
    </Card>
  );
};
